import styled from "@emotion/native"
import { Spacing, Text } from "../../ui-components"
import { useState, useRef } from "react"
import { Pressable, TextInputInstance } from "react-native"
import { CountryPickerModal } from "../CountryPicker"

type PhoneNumberInputProps = {
  phoneNumber: string
  setPhoneNumber: (phoneNumber: string) => void
  countryCode: string
  setCountryCode: (countryCode: string) => void
}

export function PhoneNumberInput({
  phoneNumber,
  setPhoneNumber,
  setCountryCode,
  countryCode,
}: PhoneNumberInputProps) {
  const inputRef = useRef<TextInputInstance>(null)
  const [showCountryPicker, setShowCountryPicker] = useState(false)

  const formatPhoneDigits = (raw: string) => {
    let digits = raw.replace(/\D/g, "")
    if (digits.length > 10 && digits.startsWith("1")) {
      digits = digits.slice(1)
    }
    digits = digits.slice(0, 10)

    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  const handlePhoneNumberInputChange = (text: string) => {
    let sanitizedText = text

    if (sanitizedText.startsWith("+1")) {
      sanitizedText = sanitizedText.replace("+1", "")
    }

    setPhoneNumber(formatPhoneDigits(sanitizedText))
  }

  return (
    <Container>
      <Text type="unnamed" color="tertiary3">
        Phone number
      </Text>
      <Spacing top={8} />
      <InputsWrapper>
        <CountryCodeWrapper
          onPress={() => setShowCountryPicker(true)}
          accessibilityRole="button"
          accessibilityLabel="Select country code"
          testID="countryCodePickerButton"
        >
          <Text type="body1" color="tertiary4">
            {countryCode}
          </Text>
        </CountryCodeWrapper>
        <PhoneNumberWrapper
          testID="phoneNumberInputContainer"
          accessibilityRole="button"
          accessibilityLabel="Phone number input"
          onPress={() => inputRef.current?.focus()}
        >
          <StyledTextInput
            ref={inputRef}
            value={phoneNumber}
            onChangeText={handlePhoneNumberInputChange}
            textContentType="telephoneNumber"
            keyboardType="phone-pad"
            placeholder="(201) 555-1232"
            placeholderTextColor="rgba(98, 96, 100, 0.5)"
            autoComplete="tel"
            autoCorrect={true}
            maxFontSizeMultiplier={1.5}
            testID="phoneNumberInput"
            autoFocus
          />
        </PhoneNumberWrapper>
      </InputsWrapper>
      <CountryPickerModal
        visible={showCountryPicker}
        onClose={() => setShowCountryPicker(false)}
        onSelect={(country) => setCountryCode(country.dial_code)}
        excludedCountryCodes={["PS"]}
        popularCountryCodes={["US", "CA"]}
      />
    </Container>
  )
}

const Container = styled.View``

const InputsWrapper = styled.View`
  flex-direction: row;
  background-color: ${({ theme }) => theme.phoneNumberInput.backgroundColor};
  height: 54px;
  border-radius: 14px;
`

const CountryCodeWrapper = styled.Pressable`
  width: 50px;
  margin-right: 13px;
  border-right-width: 1px;
  border-right-color: ${({ theme }) => theme.phoneNumberInput.countryCodeSeparatorBorderColor};
  align-items: center;
  justify-content: center;
`

const PhoneNumberWrapper = styled(Pressable)`
  flex: 1;
  justify-content: center;
`

const StyledTextInput = styled.TextInput`
  color: ${({ theme }) => theme.text.tertiary4};
  justify-content: center;
  font-size: 18px;
  flex: 1;
`
